<script setup>
import { Avatar } from '@dashhudson/dashing-ui/avatar';
import { Badge } from '@dashhudson/dashing-ui/badge';
import dayjs from 'dayjs';
import { camelizeKeys } from 'humps';
import { computed, ref } from 'vue';

import {
  COMMUNITY_COMMENT_RESOURCE_TYPES,
  COMMUNITY_INTERNAL_NOTIFICATION_MESSAGES,
} from '@/app/community/constants';
import {
  SCHEDULER_APPROVAL_REQUEST_RESOURCE_TYPE,
  SCHEDULER_APPROVALS_NOTIFICATION_MESSAGES,
  SCHEDULER_REQUEST_APPROVED_RESOURCE_TYPE,
} from '@/app/scheduler/constants';
import CommentText from '@/components/CommentingModule/CommentText.vue';
import { notificationDescriptionsByType } from '@/config';
import { useCommentingStore } from '@/stores/commenting';
import { getFormattedUserName, getUserInitials } from '@/utils/user';

const props = defineProps({
  notification: { type: Object, required: true },
  brand: { type: Object, required: true },
});

const isHovered = ref(false);

const onMouseEnter = () => {
  isHovered.value = true;
};

const onMouseLeave = () => {
  isHovered.value = false;
};

const commentingStore = useCommentingStore();

const comment = computed(() => camelizeKeys(props.notification.comment));

const author = computed(() => commentingStore?.users[props.notification.comment.userId]);

const avatarDetails = computed(() => camelizeKeys(author.value));

const authorInitials = computed(() => getUserInitials(author.value));

const authorFullName = computed(() => getFormattedUserName(author.value));

const unread = computed(() => !props.notification.readAt);

const shouldHideText = computed(() => {
  const HIDDEN_RESOURCE_TYPES = [
    COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_ASSIGNMENT,
    ...Object.values(SCHEDULER_APPROVAL_REQUEST_RESOURCE_TYPE),
    ...Object.values(SCHEDULER_REQUEST_APPROVED_RESOURCE_TYPE),
  ];

  return HIDDEN_RESOURCE_TYPES.includes(comment.value.resourceType);
});

const description = computed(() => {
  const resourceType = comment.value.resourceType;
  if (resourceType === COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_ASSIGNMENT) {
    return COMMUNITY_INTERNAL_NOTIFICATION_MESSAGES[
      COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_ASSIGNMENT
    ];
  }
  if (resourceType === COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_INTERNAL_NOTE) {
    return COMMUNITY_INTERNAL_NOTIFICATION_MESSAGES[
      COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_INTERNAL_NOTE
    ];
  }
  if (Object.values(SCHEDULER_APPROVAL_REQUEST_RESOURCE_TYPE).includes(resourceType)) {
    return SCHEDULER_APPROVALS_NOTIFICATION_MESSAGES.APPROVAL_REQUEST;
  }
  if (Object.values(SCHEDULER_REQUEST_APPROVED_RESOURCE_TYPE).includes(resourceType)) {
    return SCHEDULER_APPROVALS_NOTIFICATION_MESSAGES.REQUEST_APPROVED;
  }
  return notificationDescriptionsByType[props.notification.type];
});

const timeAgo = computed(() => {
  const time = dayjs.utc(props.notification.createdAt);
  if (dayjs.duration(dayjs().diff(time)).asSeconds() < 60) {
    return 'Just now';
  }
  return time.fromNow();
});
</script>

<template>
  <div
    :class="[
      'dash-cursor-pointer dash-px-3 dash-py-2 dash-flex dash-flex-col dash-gap-1 dash-rounded-sm-md',
      { 'dash-bg-background-utility-hover': isHovered },
    ]"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="!flex !items-center !gap-2">
      <Avatar
        :image="avatarDetails?.avatarUrl"
        :label="avatarDetails?.avatarUrl ? null : authorInitials"
        size="sm"
        class="dash-text-subtle"
      />
      <div class="dash-grow dash-w-full dash-min-w-0 dash-flex dash-items-center">
        <span class="dash-text-primary dash-typo-base" dash-truncate>{{ brand.name }}</span>
      </div>
      <div>
        <Badge v-if="unread" size="xs" />
      </div>
    </div>

    <p class="dash-typo-base">
      <span class="dash-text-primary dash-typo-base-semibold">{{ authorFullName }}</span>
      <span>&nbsp;{{ description }}<span v-if="!shouldHideText">:</span>&nbsp;</span>
      <CommentText
        v-if="!shouldHideText"
        :comment="props.notification.comment"
        :users="commentingStore.users"
        :truncate-to="100"
      />
    </p>
    <time :datetime="props.notification.createdAt" class="dash-text-subtle dash-typo-base">
      {{ timeAgo }}
    </time>
  </div>
</template>
